import { AnimatePresence } from 'framer-motion';
import React, { FC, useState } from 'react';

import { InfoCircle } from '../../../../assets/icons/index';
import { InfoCircleVariant } from '../../../../assets/icons/info-circle';
import { AppFormattedMessage } from '../../../../components/app-formatted-message/index';
import { CrossfadePanel, CrossfadeText } from '../../../../components/motion/index';
import { Radio, RadioGroup } from '../../../../components/radio-group/index';
import { SelectedCompanyCurrency } from '../../../../components/selected-company-currency/index';
import { SnapshotHooks } from '../../../../hooks/index';
import { EventsGraphDeskPlaceholder } from '../../components/events-loading-placeholders';
import { useCompanyIncorporationEvent } from '../../hooks/use-company-incorporation-event.hook';
import { GraphVariant, graphVariantMap } from '../../variables';
import { EmptyGraph } from './empty-graph';
import { EventGraph } from './event-graph';
import { valuationChartUsesShareCount } from './use-event-graph-data';

type GraphsDeskProps = {
  companyId: string;
  secondaryQueriesEnabled?: boolean;
};

export const GraphsDesk: FC<GraphsDeskProps> = ({ companyId, secondaryQueriesEnabled = true }) => {
  const [selectedGraphVariant, setGraphVariant] = useState(GraphVariant.VALUATION);
  const { snapshots, isLoading } = SnapshotHooks.useSnapshots({
    companyId,
    capTableMode: true,
    enabled: secondaryQueriesEnabled,
  });
  const { companyIncorporationEvent, isLoading: isIncorporationLoading } =
    useCompanyIncorporationEvent({
      companyId,
      enabled: secondaryQueriesEnabled,
    });
  const valuationUsesShareCount = valuationChartUsesShareCount(snapshots);

  // No phantom grants: the stored cap-table rows behind every valuation point exclude them and
  // cannot be recomputed, so a phantom-inclusive Fully Diluted point would step with no event.
  const {
    snapshot: currentSnapshot,
    isLoading: isCurrentLoading,
    isSuccess: isLoadingFinished,
  } = SnapshotHooks.useCurrent({
    companyId,
    capTableMode: true,
    enabled: secondaryQueriesEnabled,
  });

  const isGraphLoading =
    !secondaryQueriesEnabled || isLoading || isCurrentLoading || isIncorporationLoading;
  const hasGraphData =
    (snapshots.length > 0 || Boolean(companyIncorporationEvent)) && !isGraphLoading;
  const graphShellPanelKey = isGraphLoading ? 'loading' : hasGraphData ? 'graph' : 'empty';

  const graphTitleKey =
    selectedGraphVariant === GraphVariant.VALUATION ? 'valuation-history' : 'ownership-history';

  const graphSubtitleKey =
    selectedGraphVariant === GraphVariant.OWNERSHIP_PERCENT
      ? 'by-percentage'
      : selectedGraphVariant === GraphVariant.OWNERSHIP || valuationUsesShareCount
        ? 'by-shares'
        : 'by-share-price';

  const graphAxisLabelKey =
    selectedGraphVariant === GraphVariant.OWNERSHIP || valuationUsesShareCount
      ? 'shares-thousands'
      : 'currency-millions';

  return (
    <AnimatePresence mode="wait">
      {isGraphLoading ? (
        <CrossfadePanel className="w-full" panelKey="events-graph-loading">
          <EventsGraphDeskPlaceholder />
        </CrossfadePanel>
      ) : (
        <CrossfadePanel className="w-full" panelKey={`events-graph-${graphShellPanelKey}`}>
          <div className="flex h-fit w-full flex-col rounded-md p-6 shadow-sm">
            <div className="flex w-full items-center justify-between">
              <div className="flex flex-col">
                <CrossfadeText
                  className="block text-sm font-[550] text-gray-700"
                  textKey={graphTitleKey}
                >
                  {selectedGraphVariant === GraphVariant.VALUATION ? (
                    <AppFormattedMessage id="VALUATION_HISTORY" />
                  ) : (
                    <AppFormattedMessage id="OWNERSHIP_HISTORY" />
                  )}
                </CrossfadeText>
                <CrossfadeText
                  className="block text-label-sm font-medium text-gray-500"
                  textKey={graphSubtitleKey}
                >
                  {selectedGraphVariant === GraphVariant.OWNERSHIP_PERCENT ? (
                    <AppFormattedMessage id="BY_PERCENTAGE" />
                  ) : selectedGraphVariant === GraphVariant.OWNERSHIP || valuationUsesShareCount ? (
                    'By number of shares'
                  ) : (
                    'By share price'
                  )}
                </CrossfadeText>
                <div className="mt-2 flex w-full items-center gap-1 border-t border-dashed border-gray-200 pt-2">
                  <CrossfadeText
                    className="block text-label-md font-medium text-gray-500"
                    textKey={graphAxisLabelKey}
                  >
                    {selectedGraphVariant === GraphVariant.OWNERSHIP || valuationUsesShareCount ? (
                      <AppFormattedMessage id="NUMBER_OF_SHARES_IN_THOUSANDS" />
                    ) : (
                      <>
                        <AppFormattedMessage id="CURRENCY" />: <SelectedCompanyCurrency />
                        (<AppFormattedMessage id="MILLIONS" />)
                      </>
                    )}
                  </CrossfadeText>
                  <InfoCircle variant={InfoCircleVariant.EVENTS} />
                </div>
              </div>
              <div className="rounded-3xl border border-gray-200 p-1">
                <RadioGroup
                  onChange={setGraphVariant}
                  value={selectedGraphVariant}
                  wrapperClassName="w-full grow flex-1"
                >
                  {graphVariantMap.map(([graphKey, graphTitle]) => (
                    <Radio
                      className="h-8 rounded-[32px] border-transparent bg-transparent !px-3 !py-2 text-xs font-[450] text-gray-700 data-[checked]:font-[550] data-[checked]:text-brand-25"
                      key={graphKey}
                      value={graphKey}
                    >
                      <span className="text-nowrap">
                        <AppFormattedMessage id={graphTitle} />
                      </span>
                    </Radio>
                  ))}
                </RadioGroup>
              </div>
            </div>
            <div className="h-[370px] w-full">
              <AnimatePresence initial={false} mode="wait">
                {hasGraphData ? (
                  <CrossfadePanel className="h-full w-full" panelKey="event-graph">
                    <EventGraph
                      companyIncorporationEvent={companyIncorporationEvent ?? null}
                      currentSnapshot={currentSnapshot}
                      snapshots={snapshots}
                      variant={selectedGraphVariant}
                    />
                  </CrossfadePanel>
                ) : (
                  <CrossfadePanel className="h-full w-full" panelKey="empty-graph">
                    <EmptyGraph
                      isLoadingFinished={isLoadingFinished}
                      variant={selectedGraphVariant}
                    />
                  </CrossfadePanel>
                )}
              </AnimatePresence>
            </div>
          </div>
        </CrossfadePanel>
      )}
    </AnimatePresence>
  );
};
